import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useStore } from '../store/useStore';
import { AI_CONSENT_COPY, pendingActionLabel } from '../engine/aiConsent';
import { publicUrl } from '../native/native';

/**
 * AI processing consent (17 Oct 2026, App Review 5.1.2(i)). Opens the first
 * time any AI action is tapped — and again whenever AI processing is off —
 * BEFORE anything leaves the device. "Agree" is an affirmative tap (no
 * pre-ticked box, no "by continuing"); "Not now" sends nothing and leaves
 * the app usable; for Organize, "Sort on this device instead" runs the local
 * engine with no network at all. Revocable in Settings → Privacy & Data.
 * Wording: engine/aiConsent.ts (pinned by tests, quoted in review notes).
 */
export function AiConsentSheet() {
  const pending = useStore((s) => s.aiConsentPrompt);
  const grant = useStore((s) => s.grantAiConsent);
  const dismiss = useStore((s) => s.dismissAiConsentPrompt);
  const runMagicSort = useStore((s) => s.runMagicSort);
  const c = AI_CONSENT_COPY;

  return (
    <AnimatePresence>
      {pending && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 dark:bg-slate-950/90 p-4 backdrop-blur-md sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-consent-title"
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            className="w-full max-w-md space-y-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-2xl"
          >
            <h3 id="ai-consent-title" className="flex items-center gap-2 text-sm font-black text-slate-900 dark:text-white">
              <Sparkles size={15} className="text-brand-600 dark:text-brand-400" />
              {c.title}
            </h3>
            <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-300">{c.intro}</p>
            <ul className="space-y-1.5 text-xs leading-relaxed text-slate-700 dark:text-slate-300">
              {c.points.map((p) => (
                <li key={p} className="flex gap-2">
                  <span className="shrink-0 font-bold text-emerald-600 dark:text-emerald-400">✓</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
              <b>{pendingActionLabel(pending)}</b> {c.withoutAi}
            </p>
            <div className="space-y-2">
              <button
                onClick={grant}
                className="w-full rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-500"
              >
                {c.agree}
              </button>
              {pending.kind === 'sort' && (
                <button
                  onClick={() => {
                    dismiss();
                    void runMagicSort(pending.rawInput, { onDevice: true });
                  }}
                  className="w-full rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {c.sortOnDevice}
                </button>
              )}
              <div className="flex items-center justify-between px-1">
                <a
                  href={publicUrl('/privacy.html')}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] font-bold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
                >
                  {c.policy}
                </a>
                <button
                  onClick={dismiss}
                  className="rounded-lg px-3 py-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  {c.notNow}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
